import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import { FestivalProvider } from './contexts/FestivalContext';
import suppressResizeObserverLoopError from './utils/suppressResizeObserver';
import TermsAndConditionsModal from './components/TermsAndConditionsModal';
import MaintenanceGuard from './components/MaintenanceGuard';

// ============================================================================
// PERFORMANCE: Lazy Loading - Only load pages when needed
// This reduces initial bundle size by ~50%, improving first page load
// ============================================================================

// Critical pages loaded immediately (high traffic)
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';

// Other pages loaded on-demand (lazy loading)
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const VolunteerDashboard = lazy(() => import('./pages/VolunteerDashboard'));
const ShiftsPage = lazy(() => import('./pages/ShiftsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const GroupManagerDashboard = lazy(() => import('./pages/GroupManagerDashboard'));
const WorkplaceSupervisorDashboard = lazy(() => import('./pages/WorkplaceSupervisorDashboard'));
const UserProfilePage = lazy(() => import('./pages/UserProfilePage'));
const InvitationPage = lazy(() => import('./pages/InvitationPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const PartyRSVPPage = lazy(() => import('./pages/PartyRSVPPage'));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-neutral-50">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-[#f4911c] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-neutral-600">Indlæser...</p>
    </div>
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = React.createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      // Try to refresh token from cookie
      tryRefreshToken();
    }
  }, [token]);
  
  const tryRefreshToken = async () => {
    try {
      const response = await axios.post(`${API}/auth/refresh`, {}, {
        withCredentials: true // Important for sending cookies
      });
      
      // Successfully refreshed token
      login(response.data.token, response.data.user);
    } catch (error) {
      // No valid refresh token or expired
      console.log('No valid refresh token');
    } finally {
      setLoading(false);
    }
  };

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    
    // Fetch fresh user data from /api/auth/me to ensure role is up-to-date
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${newToken}` }
      });
      setUser(response.data);
    } catch (error) {
      // Fallback to userData if fetch fails
      console.error('Failed to fetch fresh user data:', error);
      setUser(userData);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const exitImpersonation = async () => {
    try {
      const response = await axios.post(`${API}/auth/stop-impersonation`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      login(response.data.token, response.data.user);
      return true;
    } catch (error) {
      console.error('Failed to exit impersonation:', error);
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, exitImpersonation, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false, supervisorOnly = false, groupManagerOnly = false }) => {
  const { user, loading } = React.useContext(AuthContext);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-600">Indlæser...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/dashboard" />;
  }
  
  if (groupManagerOnly && !['admin', 'group_manager'].includes(user.role)) {
    return <Navigate to="/dashboard" />;
  }
  
  // Allow access if user is admin, has workplace_supervisor role, OR has is_supervisor flag
  if (supervisorOnly && !['admin', 'workplace_supervisor'].includes(user.role) && !user.is_supervisor) {
    return <Navigate to="/dashboard" />;
  }
  
  return children;
};

// Global Terms Modal Wrapper - Shows terms modal for any logged-in user who hasn't accepted
const GlobalTermsModalWrapper = ({ children }) => {
  const { user, token } = React.useContext(AuthContext);
  
  return (
    <>
      {children}
      {user && token && <TermsAndConditionsModal user={user} token={token} />}
    </>
  );
};

function App() {
  useEffect(() => {
    suppressResizeObserverLoopError();
  }, []);

  return (
    <AuthProvider>
      <FestivalProvider>
        <MaintenanceGuard>
          <GlobalTermsModalWrapper>
            <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <VolunteerDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/shifts" 
                element={
                  <ProtectedRoute>
                    <ShiftsPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin" 
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/supervisor" 
              element={
                <ProtectedRoute supervisorOnly={true}>
                  <WorkplaceSupervisorDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/gruppe-admin" 
              element={
                <ProtectedRoute groupManagerOnly={true}>
                  <GroupManagerDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/user/:userId" 
              element={
                <ProtectedRoute adminOnly={true}>
                  <UserProfilePage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/invite/:token" 
              element={<InvitationPage />}
            />
            <Route 
              path="/invitation/:token" 
              element={<InvitationPage />}
            />
            <Route 
              path="/blog" 
              element={<BlogPage />}
            />
            <Route 
              path="/blog/:postId" 
              element={<BlogPage />}
            />
            <Route
              path="/rsvp/:token"
              element={<PartyRSVPPage />}
            />
          </Routes>
          </Suspense>
          </BrowserRouter>
        </GlobalTermsModalWrapper>
      </MaintenanceGuard>
    </FestivalProvider>
  </AuthProvider>
);
}

export default App;